<template>
  <div
    class="rcs-onboarding-progress"
    role="group"
    :aria-label="label"
  >
    <div class="rcs-onboarding-progress__meta">
      <span>{{ label }}</span>
      <span>{{ percent }}%</span>
    </div>
    <div
      class="rcs-onboarding-progress__track"
      role="progressbar"
      :aria-valuenow="stepNumber"
      :aria-valuemin="1"
      :aria-valuemax="totalSteps"
    >
      <div
        class="rcs-onboarding-progress__fill"
        :style="{ width: `${percent}%` }"
      />
    </div>
    <div class="rcs-onboarding-progress__steps">
      <div
        v-for="(step, index) in steps"
        :key="step.id"
        class="rcs-onboarding-progress__step"
        :class="{
          'rcs-onboarding-progress__step--active': step.id === currentStep,
          'rcs-onboarding-progress__step--done': index < (stepNumber - 1),
        }"
      >
        <span class="rcs-onboarding-progress__step-index">
          {{ index + 1 }}
        </span>
        <span class="rcs-onboarding-progress__step-label">
          {{ step.label }}
        </span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { RcsWizardStepId } from '@/types/rcs-onboarding';

export default defineComponent({
  name: 'RcsOnboardingProgress',

  props: {
    stepNumber: { type: Number, required: true },
    steps: { type: Array as () => RcsWizardStepId[], required: true },
    currentStep: { type: String as () => RcsWizardStepId, required: true },
  },

  setup(props) {
    const { t } = useI18n();
    const totalSteps = computed(() => props.steps.length);
    const percent = computed(() => (
      totalSteps.value > 0
        ? Math.round((props.stepNumber / totalSteps.value) * 100)
        : 0
    ));
    const label = computed(() => t(
      'storeParameters.services.rcsServiceParameter.nav.progress',
      { current: props.stepNumber, total: totalSteps.value },
    ));
    const labelsByStep: Record<RcsWizardStepId, string> = {
      basic_info: t('storeParameters.services.rcsServiceParameter.nav.steps.basicInfo'),
      carrier_selection: t('storeParameters.services.rcsServiceParameter.nav.steps.carrierSelection'),
      visual: t('storeParameters.services.rcsServiceParameter.nav.steps.visual'),
      representative: t('storeParameters.services.rcsServiceParameter.nav.steps.representative'),
      carrier_requirements: t('storeParameters.services.rcsServiceParameter.nav.steps.carrierRequirements'),
      test_numbers: t('storeParameters.services.rcsServiceParameter.nav.steps.testNumbers'),
    };
    const steps = computed(() => props.steps.map((id) => ({
      id,
      label: labelsByStep[id],
    })));
    return {
      totalSteps,
      percent,
      label,
      steps,
    };
  },
});
</script>
